import { useEffect } from 'react'

import { REDUCED_MOTION } from '@/lib/motion'

/**
 * Observes every [data-reveal] element and flips it to data-in="true"
 * once it enters the frame. The transition itself lives in CSS.
 *
 * Two things this has to survive:
 *
 * 1. Content that mounts AFTER the observer was built. Navigating away
 *    and back remounts the whole page, and the contact form remounts
 *    when it returns from its sent state. A one-off querySelectorAll
 *    would never see those nodes and they would sit at opacity 0
 *    forever, which reads as a blank section.
 * 2. Not running at all. The closed styles are scoped to
 *    html[data-anim='on'], so if this hook is absent the page is simply
 *    visible rather than invisible.
 */
export const useReveal = (): void => {
    useEffect(() => {
        const root = document.documentElement
        const reduced = window.matchMedia(REDUCED_MOTION).matches

        root.dataset.anim = 'on'

        const show = (node: HTMLElement): void => {
            node.dataset.in = 'true'
        }

        const waiting = (): Array<HTMLElement> =>
            Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]:not([data-in])'))

        let frame = 0
        let observer: IntersectionObserver | null = null

        if (!reduced) {
            observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return

                    show(entry.target as HTMLElement)
                    observer?.unobserve(entry.target)
                })
            }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' })
        }

        const collect = (): void => {
            const nodes = waiting()

            if (!observer) {
                nodes.forEach(show)

                return
            }

            nodes.forEach((node) => observer?.observe(node))
        }

        /* wait a frame so the closed state is painted before anything is
           observed, otherwise the first plate jumps open instead of
           transitioning */
        const schedule = (): void => {
            if (frame) return

            frame = requestAnimationFrame(() => {
                frame = 0
                collect()
            })
        }

        schedule()

        const mutations = new MutationObserver(schedule)

        mutations.observe(document.body, { childList: true, subtree: true })

        return () => {
            if (frame) cancelAnimationFrame(frame)

            mutations.disconnect()
            observer?.disconnect()
            delete root.dataset.anim
        }
    }, [])
}
